import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import { Avatar, Select, Spinner } from '../../components';
import type { SelectOption } from '../../components';
import { useAuthStore } from '../../store/authStore';
import { dashboardService, isBirthdayToday, selectedAssigneeToFilterValue } from '../../services/dashboardService';
import type {
  DashboardActivity,
  DashboardBirthday,
  DashboardStats,
  DashboardTask,
  DashboardTasksFilter,
  DashboardTasksGrouped,
} from '../../types/dashboard';
import { EMPTY_DASHBOARD_TASKS, EMPTY_DASHBOARD_TASKS_FILTER } from '../../types/dashboard';
import { formatCompactIndianRupee } from '../../utils/formatters';
import { getDefaultTaskAssigneeFilter } from '../../utils/taskAssigneeFilter';
import { canViewDashboardStats } from '../../utils/rbac';
import './Dashboard.scss';

type DashboardIconName =
  | 'clients'
  | 'paid'
  | 'leads'
  | 'revenue'
  | 'subscriptions'
  | 'rocket'
  | 'cake'
  | 'mail';

interface StatCard {
  label: string;
  value: string;
  icon: DashboardIconName;
  tone: 'gray' | 'blue' | 'gold' | 'green' | 'purple';
  href?: string;
}

const formatStatNumber = (value: number): string =>
  new Intl.NumberFormat('en-IN').format(value);

type TaskTab = 'today' | 'tomorrow' | 'upcoming';

const TASK_TAB_BUCKET: Record<TaskTab, keyof DashboardTasksGrouped> = {
  today: 'dueToday',
  tomorrow: 'dueTomorrow',
  upcoming: 'dueIn7Days',
};

const TASK_TAB_EMPTY: Record<TaskTab, string> = {
  today: 'No tasks due today.',
  tomorrow: 'No tasks due tomorrow.',
  upcoming: 'No tasks due in the next 7 days.',
};

const buildStats = (stats: DashboardStats): StatCard[] => [
  { label: 'Total Clients', value: formatStatNumber(stats.totalClients), icon: 'clients', tone: 'gray', href: '/users' },
  { label: 'Paid Clients', value: formatStatNumber(stats.paidClients), icon: 'paid', tone: 'blue', href: '/users?paid=true' },
  { label: 'Total Leads', value: formatStatNumber(stats.totalLeads), icon: 'leads', tone: 'gold', href: '/leads' },
  { label: 'Revenue Summary', value: formatCompactIndianRupee(stats.revenueSummary), icon: 'revenue', tone: 'green', href: '/user-plans' },
  {
    label: 'Active Subscriptions',
    value: formatStatNumber(stats.activeSubscriptions),
    icon: 'mail',
    tone: 'purple',
    href: '/user-plans?status=active',
  },
];

const DashboardIcon: React.FC<{ name: DashboardIconName }> = ({ name }) => {
  switch (name) {
    case 'clients':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8.5 11.5a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z" />
          <path d="M15.8 10.8a2.6 2.6 0 1 0 0-5.2 2.6 2.6 0 0 0 0 5.2Z" />
          <path d="M3.8 18.8c.4-2.8 2.3-4.5 4.7-4.5s4.4 1.7 4.7 4.5" />
          <path d="M13.9 14.3c2.7.1 4.6 1.7 5 4.5" />
        </svg>
      );
    case 'paid':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3.5" y="6" width="17" height="12" rx="2" />
          <path d="M3.5 9.5h17" />
          <path d="M8 14.5h4.8" />
          <path d="M15.7 14.5h1.7" />
        </svg>
      );
    case 'leads':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 5v9" />
          <path d="M7.5 9.5h9" />
          <path d="M6 19c.5-2.8 2.9-4.8 6-4.8s5.5 2 6 4.8" />
          <path d="M12 14.2a3.3 3.3 0 1 0 0-6.6" />
        </svg>
      );
    case 'revenue':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 17.5h14" />
          <path d="M7 15v2.5" />
          <path d="M11 11v6.5" />
          <path d="M15 8v9.5" />
          <path d="m6.8 11.5 4-4 3 2.8L18 5.8" />
        </svg>
      );
    case 'subscriptions':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="4" y="6" width="16" height="12" rx="2" />
          <path d="m7 10 5 4 5-4" />
        </svg>
      );
    case 'rocket':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 14.5 8.7 11.2C9.8 7.5 12.5 5 17.5 4.5 17 9.5 14.5 12.2 10.8 13.3Z" />
          <path d="m8.5 11.5-3 .8 2.2 2.2.8-3Z" />
          <path d="m12.5 15.5-.8 3 2.2-2.2.8-3Z" />
          <path d="M15 8.2h.1" />
        </svg>
      );
    case 'cake':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 11h14v8a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-8Z" />
          <path d="M5 11c0-2.2 1.8-4 4-4 .9 0 1.7.3 2.3.8.6-.5 1.4-.8 2.3-.8 2.2 0 4 1.8 4 4" />
          <path d="M9 7V5.5" />
          <path d="M12 7V4.5" />
          <path d="M15 7V5.5" />
        </svg>
      );
    case 'mail':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="5" y="7" width="14" height="10" rx="2" />
          <path d="m7.5 10 4.5 3.2 4.5-3.2" />
        </svg>
      );
    default:
      return null;
  }
};

const StatCardView: React.FC<{ stat: StatCard; onNavigate: (path: string) => void }> = ({
  stat,
  onNavigate,
}) => {
  const content = (
    <>
      <span className={`dashboard-icon-badge dashboard-icon-badge--${stat.tone}`}>
        <DashboardIcon name={stat.icon} />
      </span>
      <span className="dashboard-stat-card__label">{stat.label}</span>
      <strong className="dashboard-stat-card__value">{stat.value}</strong>
    </>
  );

  if (stat.href) {
    return (
      <button
        type="button"
        className="dashboard-stat-card dashboard-stat-card--clickable"
        onClick={() => onNavigate(stat.href!)}
        aria-label={`View ${stat.label}`}
      >
        {content}
      </button>
    );
  }

  return <article className="dashboard-stat-card">{content}</article>;
};

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const role = useAuthStore((state) => state.role);
  const showFullDashboard = canViewDashboardStats(role);
  const displayName =
    user?.name?.trim() ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email?.split('@')[0] ||
    'there';

  const [activeTaskTab, setActiveTaskTab] = useState<TaskTab>('today');
  const [assigneeFilter, setAssigneeFilter] = useState(getDefaultTaskAssigneeFilter);
  const [tasksFilter, setTasksFilter] = useState<DashboardTasksFilter>(EMPTY_DASHBOARD_TASKS_FILTER);
  const [stats, setStats] = useState<StatCard[]>([]);
  const [taskBuckets, setTaskBuckets] = useState<DashboardTasksGrouped>(EMPTY_DASHBOARD_TASKS);
  const [activities, setActivities] = useState<DashboardActivity[]>([]);
  const [birthdays, setBirthdays] = useState<DashboardBirthday[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTasksLoading, setIsTasksLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendingBirthdayUserId, setSendingBirthdayUserId] = useState<string | null>(null);

  const loadTasks = useCallback(async (assignee: string) => {
    setIsTasksLoading(true);
    try {
      const result = await dashboardService.getTasks(assignee);
      setTaskBuckets(result.tasks);
      setTasksFilter(result.filter);
      setAssigneeFilter(selectedAssigneeToFilterValue(result.filter.selectedAssigneeUserId));
    } catch (err) {
      console.error('Failed to load dashboard tasks:', err);
      setTaskBuckets(EMPTY_DASHBOARD_TASKS);
    } finally {
      setIsTasksLoading(false);
    }
  }, []);

  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [birthdays, activity] = await Promise.all([
        dashboardService.getUpcomingBirthdays(),
        dashboardService.getActivityFeed(),
      ]);
      setActivities(activity);
      setBirthdays(birthdays);

      if (showFullDashboard) {
        const stats = await dashboardService.getStats();
        setStats(buildStats(stats));
      }
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  }, [showFullDashboard]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    void loadTasks(assigneeFilter);
  }, [assigneeFilter, loadTasks]);

  const handleAssigneeFilterChange = useCallback((value: string) => {
    setAssigneeFilter(value);
  }, []);

  const assigneeOptions: SelectOption[] = useMemo(
    () =>
      tasksFilter.employees.map((employee) => ({
        value: employee.userId === null ? 'all' : String(employee.userId),
        label: employee.name,
      })),
    [tasksFilter.employees]
  );

  const handleSendBirthdayEmail = useCallback(
    async (userId: string) => {
      if (sendingBirthdayUserId) return;
      setSendingBirthdayUserId(userId);
      try {
        await dashboardService.sendBirthdayEmail(userId);
      } catch (err) {
        console.error('Failed to send birthday email:', err);
      } finally {
        setSendingBirthdayUserId(null);
      }
    },
    [sendingBirthdayUserId]
  );

  const activeBucketKey = TASK_TAB_BUCKET[activeTaskTab];
  const activeBucket = taskBuckets[activeBucketKey];
  const visibleTasks = activeBucket.tasks;

  const openTask = useCallback(
    (taskId: string) => {
      navigate('/kanban', { state: { openTaskId: taskId } });
    },
    [navigate]
  );

  return (
    <div className="dashboard-page">
      <header className="dashboard-hero">
        <h1>Welcome back, {displayName}!</h1>
        <p>Here is what is happening with Saathi4u today.</p>
      </header>

      {error && (
        <div className="dashboard-error">
          <p>{error}</p>
          <button type="button" onClick={() => { void loadDashboard(); void loadTasks(assigneeFilter); }}>
            Retry
          </button>
        </div>
      )}

      {isLoading && stats.length === 0 && activities.length === 0 ? (
        <div className="dashboard-loading">
          <Spinner size="medium" />
        </div>
      ) : (
        <>
      {showFullDashboard && (
      <section className="dashboard-stats" aria-label="Key metrics">
        {(stats.length > 0 ? stats : buildStats({
          totalClients: 0,
          paidClients: 0,
          totalLeads: 0,
          revenueSummary: 0,
          activeSubscriptions: 0,
        })).map((stat) => (
          <StatCardView key={stat.label} stat={stat} onNavigate={navigate} />
        ))}
      </section>
      )}

      <div className="dashboard-section-title">
        <span className="dashboard-section-title__icon">
          <DashboardIcon name="rocket" />
        </span>
        <h2>Quick Shortcuts</h2>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-grid__main">
          <section className="dashboard-card dashboard-tasks">
            <div className="dashboard-card__header dashboard-card__header--tabs">
              <h3>Tasks Management</h3>
              <div className="dashboard-tasks__filters">
                <div className="dashboard-tasks__assignee-select">
                  <Select
                    value={assigneeFilter}
                    onChange={handleAssigneeFilterChange}
                    options={assigneeOptions}
                    searchable={tasksFilter.canViewAll}
                    disabled={!tasksFilter.canViewAll}
                    fullWidth={false}
                    className="dashboard-tasks__select"
                    placeholder="Employee"
                  />
                  <ChevronDownIcon
                    className="dashboard-tasks__select-chevron"
                    aria-hidden="true"
                  />
                </div>
                <div className="dashboard-tabs" aria-label="Task date filters">
                <button
                  type="button"
                  className={`dashboard-tabs__item ${activeTaskTab === 'today' ? 'dashboard-tabs__item--active' : ''}`}
                  onClick={() => setActiveTaskTab('today')}
                >
                  Today{taskBuckets.dueToday.total > 0 ? ` (${taskBuckets.dueToday.total})` : ''}
                </button>
                <button
                  type="button"
                  className={`dashboard-tabs__item ${activeTaskTab === 'tomorrow' ? 'dashboard-tabs__item--active' : ''}`}
                  onClick={() => setActiveTaskTab('tomorrow')}
                >
                  Tomorrow{taskBuckets.dueTomorrow.total > 0 ? ` (${taskBuckets.dueTomorrow.total})` : ''}
                </button>
                <button
                  type="button"
                  className={`dashboard-tabs__item ${activeTaskTab === 'upcoming' ? 'dashboard-tabs__item--active' : ''}`}
                  onClick={() => setActiveTaskTab('upcoming')}
                >
                  Upcoming{taskBuckets.dueIn7Days.total > 0 ? ` (${taskBuckets.dueIn7Days.total})` : ''}
                </button>
              </div>
              </div>
            </div>

            <div className="dashboard-task-list">
              {isTasksLoading && (
                <div className="dashboard-task-list__loading">
                  <Spinner size="small" />
                </div>
              )}
              {visibleTasks.length === 0 && !isLoading && !isTasksLoading && (
                <p className="dashboard-empty">{TASK_TAB_EMPTY[activeTaskTab]}</p>
              )}
              {visibleTasks.map((task) => (
                <article
                  className="dashboard-task"
                  key={task.id}
                  onClick={() => openTask(task.taskId)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openTask(task.taskId);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="dashboard-task__body">
                    <strong>{task.title}</strong>
                    <div className="dashboard-task__meta">
                      <Avatar
                        name={task.assignee.name}
                        src={task.assignee.avatarUrl ?? undefined}
                        size="small"
                        className="dashboard-task__avatar"
                      />
                      <span className="dashboard-task__assignee">{task.assignee.name}</span>
                      <span className="dashboard-task__date">
                        <CalendarIcon aria-hidden="true" />
                        {task.dueDate}
                      </span>
                    </div>
                  </div>
                  <span className="dashboard-task__status">
                    <span aria-hidden="true" />
                    {task.status}
                  </span>
                </article>
              ))}
            </div>

            <button
              type="button"
              className="dashboard-card__footer-action"
              onClick={() => navigate('/kanban')}
            >
              View all tasks
            </button>
          </section>

          <section className="dashboard-card dashboard-activity">
            <div className="dashboard-card__header">
              <h3>Recent Activity Feed</h3>
            </div>

            <div className="dashboard-activity__list">
              {activities.length === 0 && !isLoading && (
                <p className="dashboard-empty">No recent activity.</p>
              )}
              {activities.map((activity) => (
                <article className="dashboard-activity__item" key={activity.id}>
                  <span
                    className={`dashboard-activity__dot dashboard-activity__dot--${activity.indicatorColor}`}
                    aria-hidden="true"
                  />
                  <div>
                    <strong>
                      {activity.title}
                      {activity.description ? `: ${activity.description}` : ''}
                    </strong>
                    <p>
                      {activity.relativeTime}
                      {activity.source ? ` · ${activity.source}` : ''}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>

        <aside className="dashboard-grid__side" aria-label="Dashboard reminders">
          <section className="dashboard-card dashboard-birthdays">
            <div className="dashboard-card__header">
              <h3>Upcoming Birthdays</h3>
              <span className="dashboard-birthdays__cake">
                <DashboardIcon name="cake" />
              </span>
            </div>

            <div className="dashboard-birthdays__list">
              {birthdays.length === 0 && !isLoading && (
                <p className="dashboard-empty">No upcoming birthdays.</p>
              )}
              {birthdays.map((birthday) => {
                const isToday = isBirthdayToday(birthday.dateOfBirth);
                const dateLabel = isToday ? 'Today' : birthday.birthdayLabel;
                const isSending = sendingBirthdayUserId === birthday.userId;
                return (
                  <article
                    className={`dashboard-birthday${isToday ? ' dashboard-birthday--today' : ''}`}
                    key={birthday.userId}
                  >
                    <Avatar
                      name={birthday.name}
                      src={birthday.profilePictureUrl ?? undefined}
                      size="small"
                    />
                    <div>
                      <Link to={`/users/${birthday.userId}`} className="dashboard-birthday__name">
                        {birthday.name}
                      </Link>
                      <span>
                        {birthday.occupation
                          ? `${birthday.occupation} · ${dateLabel}`
                          : dateLabel}
                      </span>
                    </div>
                    {isToday && (
                      <button
                        type="button"
                        className="dashboard-birthday__email-btn"
                        aria-label={`Send birthday email to ${birthday.name}`}
                        disabled={isSending || sendingBirthdayUserId !== null}
                        onClick={() => void handleSendBirthdayEmail(birthday.userId)}
                      >
                        {isSending ? (
                          <Spinner size="small" />
                        ) : (
                          <DashboardIcon name="mail" />
                        )}
                      </button>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        </aside>
      </div>
        </>
      )}
    </div>
  );
};
